import React, { useState, useRef, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import emailjs from "emailjs-com";
import ReCAPTCHA from "react-google-recaptcha";

// Constants for rate limiting
const MAX_SUBMISSIONS_PER_DAY = 3;
const SUBMISSION_STORAGE_KEY = "newsletter_submissions";

const EmailSubscribeFooter = () => {
  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(); // Reference for reCAPTCHA
  const formRef = useRef(); // Create a reference for the form
  const [formStartTime, setFormStartTime] = useState(null);
  const [honeypot, setHoneypot] = useState(""); // Honeypot field
  const [submissions, setSubmissions] = useState([]);

  // Initialize form start time
  useEffect(() => {
    setFormStartTime(Date.now());
  }, []);

  // Load and validate submission history
  useEffect(() => {
    try {
      const storedData = localStorage.getItem(SUBMISSION_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        // Filter out submissions older than 24 hours
        const validSubmissions = parsedData.filter((timestamp) => {
          return Date.now() - timestamp < 24 * 60 * 60 * 1000;
        });
        setSubmissions(validSubmissions);
        // Update storage with only valid submissions
        localStorage.setItem(
          SUBMISSION_STORAGE_KEY,
          JSON.stringify(validSubmissions)
        );
      }
    } catch (error) {
      console.error("Error loading submission history:", error);
      // Reset if there's an error
      localStorage.setItem(SUBMISSION_STORAGE_KEY, JSON.stringify([]));
      setSubmissions([]);
    }
  }, []);

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const canSubmit = () => {
    return submissions.length < MAX_SUBMISSIONS_PER_DAY;
  };

  const recordSubmission = () => {
    const newSubmissions = [...submissions, Date.now()];
    setSubmissions(newSubmissions);
    localStorage.setItem(
      SUBMISSION_STORAGE_KEY,
      JSON.stringify(newSubmissions)
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Check submission limit
    if (!canSubmit()) {
      toast.error(
        `You can only subscribe ${MAX_SUBMISSIONS_PER_DAY} times per day. Please try again tomorrow.`,
        { position: "bottom-left" }
      );
      return;
    }

    // Check honeypot field
    if (honeypot !== "") {
      console.log("Spam detected - honeypot field filled");
      return;
    }

    // Check submission time
    const submissionTime = Date.now();
    const timeToFill = submissionTime - formStartTime;
    if (timeToFill < 4000) {
      // Less than 20 seconds
      toast.error(
        "You have submitted the form quite frequently. Please wait a moment before trying again.",
        { position: "bottom-left" }
      );
      return;
    }

    // Simple email validation
    if (!email) {
      toast.error("Please enter a valid email address.", {
        position: "bottom-left",
      });
      return;
    }

    // Validate CAPTCHA
    if (!captchaToken) {
      toast.error(
        <>
          <div className="text-start">
            Google reCAPTCHA verification failed, please try again later.
          </div>
        </>,
        { position: "bottom-left" }
      );
      return;
    }

    // Send email
    emailjs
      .sendForm(
        "service_hic",
        "template_newsletter",
        formRef.current,
        "62L2S7468wThNb16U"
      )
      .then(
        (response) => {
          // Record successful submission
          recordSubmission();

          toast.success("Thank you for subscribing to our newsletter!", {
            position: "bottom-left",
          });
          setEmail(""); // Reset email field
          captchaRef.current.reset(); // Reset reCAPTCHA
          setHoneypot(""); // Reset honeypot
        },
        (error) => {
          console.log(error);
          toast.error("An error occurred while submitting the form.", {
            position: "bottom-left",
          });
        }
      );
  };

  return (
    <>
      <ToastContainer />
      <section className="container-fluid text-white">
        <div className="text-center">
          <div className="footer-fs-middle-banner fw-500 text-ss-secondary lh-sm text-hidayatullah">
            Newsletter
          </div>
          <div className="footer-fs-sub-middle-banner fw-500">
            Subscribe to our mailing list
          </div>
          <div className="mt-4">
            <a
              target="_blank"
              href="https://us.mohid.co/al/huntsville/hicm/masjid/online/vfr/campaign/ramadhan_fundraising"
              data-bs-toggle="modal"
              data-bs-target="#exampleModal"
              className="hover-ss-donate text-decoration-none bg-ss-secondary rounded-5 border-0 py-2 px-5 fs-ss-22 text-dark fw-500"
            >
              Signup
            </a>
          </div>

          <div
            className="mt-5 mt-md-0 modal modal-lg fade"
            id="exampleModal"
            tabIndex="-1"
            aria-labelledby="exampleModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body bg-above-footer py-5">
                  <div className="pb-4">
                    <div className="fs-ss-56 fw-500 text-ss-secondary lh-sm text-hidayatullah">
                      Newsletter
                    </div>
                    <div className="fs-sub-middle-banner fw-500">
                      Subscribe to our mailing list
                    </div>
                  </div>

                  <form ref={formRef} onSubmit={handleSubmit}>
                    {/* Honeypot field - hidden from users but visible to bots */}
                    <div style={{ display: "none" }}>
                      <input
                        type="text"
                        name="company"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                      />
                    </div>

                    <div className="col-md-10 mt-2 mt-md-3 d-flex mx-auto bg-white">
                      <input
                        className="py-2 form-control input-focus rounded-0 border-0"
                        placeholder="Email"
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />

                      <button
                        type="submit"
                        className="col-4 d-block d-md-none bg-ss-primary text-white rounded-0 border-0 py-1 fs-ss-20 fw-500 my-1 me-1"
                      >
                        Sign up
                      </button>

                      <button
                        type="submit"
                        className="w-50 d-none d-md-block bg-ss-primary text-white rounded-0 border-0 py-1 fs-ss-20 fw-500 my-1 me-1"
                      >
                        Sign up
                      </button>
                    </div>

                    <div className="col-md-10 mx-auto text-start mt-4">
                      <ReCAPTCHA
                        sitekey="6LeRAMYqAAAAAPkJ56Nnqdgmmsm9QgnISv0tpiMX"
                        onChange={handleCaptchaChange}
                        ref={captchaRef}
                      />
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default EmailSubscribeFooter;
